(function () {
    /********************* Setup *********************/

        // find the section element via the script's data-id
    const sectionID = document.currentScript.dataset.id;
    const section = document.querySelector(`[id="${sectionID}"]`);
    if (!section) {
        console.warn(`Concierge section with ID ${sectionID} not found.`);
        return;
    }

    const nav = section.querySelector(".conciergeV1Nav");
    if (!nav) {
        return;
    }

    // there is no login. the customer proves the order is theirs on the Find
    // Your Order screen, and every later call re-sends the same three fields;
    // the API re-checks them, which is one indexed lookup and saves having a
    // session to mint, expire, and revoke
    const API = "https://api.audryrosejewelry.com/public/concierge";

    // what the customer picks, screen by screen
    let request = {};
    let auth = {};
    let order = null;
    let pendingRequest = "";
    let photos = [];
    let screenIndex = 0;

    const RESOLUTIONS = [
        {value: "refund", label: "Return for a refund", requires: "return_eligible"},
        {value: "exchange_size", label: "Exchange for a different size", requires: "exchange_eligible"},
        {value: "exchange_piece", label: "Exchange for another piece", requires: "exchange_eligible"},
        {value: "store_credit", label: "Receive store credit", requires: "return_eligible"},
    ];

    const REPAIR_TYPES = [
        {value: "resize", label: "Resize"},
        {value: "stone_loose", label: "A stone came loose or fell out (I have the stone)"},
        {value: "stone_lost", label: "A stone fell out (stone is lost)"},
        {value: "chain_clasp", label: "Chain or clasp issue"},
        {value: "bent_damaged", label: "Bent or damaged"},
        {value: "cleaning", label: "Cleaning & inspection"},
        {value: "other", label: "Something else"},
    ];

    /********************* Screens *********************/

    // the screens this request walks, which depends on the path taken
    function screenSequence() {
        // "check on a request" only needs the order looked up; the lookup itself
        // hands the customer straight to their journey
        if (request.intent === "status") {
            return ["intent", "order"];
        }

        if (request.intent === "repair") {
            return ["intent", "order", "piece", "repair", "photos", "details"];
        }

        const screens = ["intent", "order", "piece", "resolution", "reason"];

        // the size question only makes sense for a size exchange
        if (request.resolution === "exchange_size") {
            screens.push("size");
        }

        screens.push("details");
        return screens;
    }

    // show one screen and rebuild the footer to match it
    function showScreen(name) {
        section.querySelectorAll(".conciergeV1Screen").forEach(screen => {
            screen.classList.toggle("active", screen.dataset.screen === name);
        });

        clearMessage();
        renderNav(name);
        window.scrollTo({top: section.offsetTop - 40, behavior: "smooth"});
    }

    // the footer is contextual: no Back on the first screen, and the last
    // screen submits rather than continuing
    function renderNav(name) {
        const sequence = screenSequence();
        const isFlowScreen = sequence.includes(name);

        nav.classList.toggle("active", isFlowScreen && name !== "intent");

        if (!isFlowScreen) {
            return;
        }

        const button = nav.querySelector(".conciergeV1NavContinue");

        let label = "Continue";
        if (name === "details") {
            label = "Submit Request";
        }
        if (name === "order") {
            label = "Find My Order";
        }

        // the photo step is explicitly optional, so say so on the button
        if (name === "photos" && photos.length === 0) {
            label = "Skip";
        }

        button.textContent = label;
    }

    // move forward if the current screen is satisfied
    async function goForward() {
        const sequence = screenSequence();
        const current = sequence[screenIndex];

        // the lookup screen is the one that talks to the server to advance
        if (current === "order") {
            await lookupOrder();
            return;
        }

        if (!validateScreen(current)) {
            return;
        }

        // the last screen submits instead of advancing
        if (current === "details") {
            await submitRequest();
            return;
        }

        screenIndex += 1;

        // recompute after the move: choosing a size exchange inserts a screen
        const next = screenSequence()[screenIndex];
        await enterScreen(next);
    }

    // step back through the same sequence
    function goBack() {
        if (screenIndex === 0) {
            return;
        }

        screenIndex -= 1;
        showScreen(screenSequence()[screenIndex]);
    }

    // load whatever a screen needs before showing it
    async function enterScreen(name) {
        if (name === "piece") {
            renderPieces();
        }
        if (name === "resolution") {
            renderResolutions();
        }
        if (name === "size") {
            renderSizes();
        }
        if (name === "repair") {
            renderRepairTypes();
        }
        if (name === "details") {
            fillAddress();
            renderPayment();
        }

        showScreen(name);
    }

    // each screen states its own requirement, so the footer stays generic
    function validateScreen(name) {
        if (name === "piece" && selectedItems.length === 0) {
            showMessage("Choose the piece we can help with.");
            return false;
        }
        if (name === "resolution" && !request.resolution) {
            showMessage("Let us know what you'd like to do.");
            return false;
        }
        if (name === "repair" && !request.repair_type) {
            showMessage("Let us know how we can help.");
            return false;
        }
        if (name === "size") {
            // every piece needs an answer, even if that answer is "not sure yet"
            const rows = Array.from(section.querySelectorAll(".conciergeV1SizesItem"));
            const missing = rows.some(row => {
                const deciding = row.querySelector(".conciergeV1SizesItemDeciding").checked;
                const size = row.querySelector(".conciergeV1SizesItemInput").value.trim();
                return !deciding && !size;
            });

            if (missing) {
                showMessage("Tell us a size for each piece, or let us know you're still deciding.");
                return false;
            }
        }
        if (name === "details") {
            return validateAddress();
        }

        return true;
    }

    /********************* Lookup *********************/

    // verify the order and keep the details for the rest of the visit
    async function lookupOrder() {
        const orderNumber = section.querySelector(".conciergeV1LookupOrder").value.trim();
        const emailZip = section.querySelector(".conciergeV1LookupEmailZip").value.trim();

        if (!orderNumber) {
            showMessage("Please enter your order number.");
            return;
        }
        if (!emailZip) {
            showMessage("Please enter the email address or ZIP code from your order.");
            return;
        }

        // one box takes either, and only an email has an @
        let email = "";
        let zip = "";

        if (emailZip.includes("@")) {
            email = emailZip;
        } else {
            zip = emailZip;
        }

        setBusy(true);

        // held for the rest of the visit and re-sent on every later call
        auth = {order_number: orderNumber, email: email, zip: zip};

        const response = await post(`${API}/lookup.php`, auth);

        setBusy(false);

        if (!response) {
            return;
        }

        order = response.order;
        selectedItems = [];

        // "check on a request", or an emailed link, wanted the lookup rather
        // than the wizard
        if (request.intent === "status") {
            await renderJourneys(pendingRequest);
            showScreen("status");
            return;
        }

        screenIndex += 1;
        await enterScreen("piece");
    }

    /********************* Render *********************/

    // the order's pieces, each with what we can offer for it
    function renderPieces() {
        const container = section.querySelector(".conciergeV1Pieces");

        if (!order) {
            container.innerHTML = "";
            return;
        }

        const multi = request.intent !== "repair";

        // the heading and the hint both change with how many can be picked
        let title = "Which piece can we help with?";
        if (multi) {
            title = "Which pieces can we help with?";
        }
        section.querySelector(".conciergeV1ScreenPiece .conciergeV1ScreenIntro").textContent = title;

        let html = "";
        order.line_items.forEach(item => {
            const eligibility = item.eligibility || {};

            // what this piece qualifies for on the path the customer picked
            let allowed = eligibility.repair_eligible;
            if (request.intent === "return") {
                allowed = eligibility.return_eligible || eligibility.exchange_eligible;
            }

            let disabledClass = "";
            let noteHTML = "";

            if (item.open_request) {
                allowed = false;
                noteHTML = `<span class="conciergeV1PiecesItemNote">Already in progress as ${escapeHtml(item.open_request)}</span>`;
            } else if (!allowed) {
                let reason = "This piece isn't eligible. Your Concierge can still help by email.";
                if ((eligibility.reasons || []).length > 0) {
                    reason = eligibility.reasons[0];
                }
                noteHTML = `<span class="conciergeV1PiecesItemNote">${escapeHtml(reason)}</span>`;
            }

            if (!allowed) {
                disabledClass = " conciergeV1PiecesItemDisabled";
            }

            let variantHTML = "";
            if (item.variant_title) {
                variantHTML = `<span class="conciergeV1PiecesItemVariant">${escapeHtml(item.variant_title)}</span>`;
            }

            // a tick box says "choose as many as you like" without a hint
            let tickHTML = "";
            if (multi && allowed) {
                tickHTML = `<span class="conciergeV1PiecesItemTick"></span>`;
            }

            let activeClass = "";
            if (selectedItems.includes(item.id)) {
                activeClass = " active";
            }

            html += `
                <button class="conciergeV1PiecesItem${disabledClass}${activeClass}" data-line-item-id="${escapeHtml(item.id)}">
                    ${tickHTML}
                    <span class="conciergeV1PiecesItemTitle">${escapeHtml(item.product_title)}</span>
                    ${variantHTML}
                    ${noteHTML}
                </button>
            `;
        });

        container.innerHTML = html;
    }

    // only offer resolutions the piece actually qualifies for
    function renderResolutions() {
        const container = section.querySelector(".conciergeV1OptionsResolution");
        const chosen = selectedLineItems();

        let html = "";
        RESOLUTIONS.forEach(option => {
            // offered only when every chosen piece qualifies, since one answer
            // covers them all. a piece that narrows the options is better than
            // an option that fails at submit
            const allQualify = chosen.every(item => (item.eligibility || {})[option.requires]);
            if (!allQualify) {
                return;
            }

            html += `
                <label class="conciergeV1OptionsItem">
                    <input class="conciergeV1OptionsItemInput" type="radio" name="conciergeResolution" value="${option.value}">
                    <span class="conciergeV1OptionsItemLabel">${option.label}</span>
                </label>
            `;
        });

        if (html === "") {
            html = `<p class="conciergeV1OptionsEmpty">We can't offer the same option for every piece you picked. Try them one at a time, or email your Concierge and we'll sort it out.</p>`;
        }

        container.innerHTML = html;
    }

    // one size row per chosen piece, since sizes differ per piece
    function renderSizes() {
        const container = section.querySelector(".conciergeV1Sizes");

        let html = "";
        selectedLineItems().forEach(item => {
            let variant = "";
            if (item.variant_title) {
                variant = ` (${item.variant_title})`;
            }

            html += `
                <div class="conciergeV1SizesItem" data-line-item-id="${escapeHtml(item.id)}">
                    <span class="conciergeV1SizesItemTitle">${escapeHtml(item.product_title + variant)}</span>
                    <input class="conciergeV1FieldInput conciergeV1SizesItemInput" type="text" placeholder="e.g. 6.5">
                    <label class="conciergeV1FieldInline">
                        <input class="conciergeV1FieldInlineCheckbox conciergeV1SizesItemDeciding" type="checkbox">
                        <span class="conciergeV1FieldInlineLabel">I'm still deciding</span>
                    </label>
                </div>
            `;
        });

        container.innerHTML = html;
    }

    // the repair menu is the same for every piece
    function renderRepairTypes() {
        const container = section.querySelector(".conciergeV1OptionsRepair");

        let html = "";
        REPAIR_TYPES.forEach(option => {
            html += `
                <label class="conciergeV1OptionsItem">
                    <input class="conciergeV1OptionsItemInput" type="radio" name="conciergeRepair" value="${option.value}">
                    <span class="conciergeV1OptionsItemLabel">${option.label}</span>
                </label>
            `;
        });

        container.innerHTML = html;
    }

    // the chosen pieces as full objects, in the order they appear on the order
    function selectedLineItems() {
        if (!order) {
            return [];
        }

        return order.line_items.filter(item => selectedItems.includes(item.id));
    }

    // prefill the address from the order, editable
    function fillAddress() {
        const address = (order || {}).shipping_address || {};

        section.querySelector(".conciergeV1AddressName").value = address.name || "";
        section.querySelector(".conciergeV1AddressStreet1").value = address.street1 || "";
        section.querySelector(".conciergeV1AddressStreet2").value = address.street2 || "";
        section.querySelector(".conciergeV1AddressCity").value = address.city || "";
        section.querySelector(".conciergeV1AddressState").value = address.state || "";
        section.querySelector(".conciergeV1AddressZip").value = address.zip || "";
        section.querySelector(".conciergeV1AddressPhone").value = address.phone || "";
        section.querySelector(".conciergeV1AddressEmail").value = address.email || "";
    }

    // an exchange may leave a balance either way, so say who we would settle it
    // with. Shopify no longer exposes card details on the order, so the gateway
    // name is the most that can honestly be shown
    function renderPayment() {
        const panel = section.querySelector(".conciergeV1Payment");
        const isExchange = request.resolution === "exchange_size" || request.resolution === "exchange_piece";

        if (!isExchange) {
            panel.classList.remove("active");
            panel.innerHTML = "";
            return;
        }

        const chosen = selectedLineItems();
        const gateways = ((chosen[0] || {}).money || {}).payment_gateways || [];

        let method = "the original payment method";
        if (gateways.length > 0) {
            method = gateways.join(", ");
        }

        panel.innerHTML = `
            <span class="conciergeV1PaymentTitle">Payment</span>
            <span class="conciergeV1PaymentBody">Your order was paid by ${escapeHtml(method)}. If your exchange leaves a difference either way, your Concierge will be in touch to settle it before anything ships.</span>
        `;
        panel.classList.add("active");
    }

    // the branded journey, one card per request
    async function renderJourneys(requestNumber = "") {
        const container = section.querySelector(".conciergeV1Journeys");
        container.innerHTML = `<p class="conciergeV1JourneysEmpty">Loading your journey...</p>`;

        // an emailed link names one request; otherwise show every request on
        // the order
        const payload = {order_number: auth.order_number, email: auth.email, zip: auth.zip};
        if (requestNumber) {
            payload.request = requestNumber;
        }

        const entries = await post(`${API}/requests.php`, payload);
        if (!entries || entries.length === 0) {
            container.innerHTML = `<p class="conciergeV1JourneysEmpty">We don't have anything to show for this order yet.</p>`;
            return;
        }

        let html = "";
        entries.forEach(entry => {
            let stepsHTML = "";
            entry.journey.forEach(step => {
                let dateHTML = "";
                if (step.at) {
                    const stamp = new Date(step.at).toLocaleDateString("en-US", {
                        year: "numeric", month: "long", day: "numeric",
                    });
                    dateHTML = `<span class="conciergeV1JourneysCardStepDate">${escapeHtml(stamp)}</span>`;
                }

                // the tracking number belongs on the step that earned it
                let trackingHTML = "";
                if (step.meta && step.meta.tracking) {
                    trackingHTML = `<span class="conciergeV1JourneysCardStepTracking">Tracking: ${escapeHtml(step.meta.tracking)}</span>`;
                }

                stepsHTML += `
                    <li class="conciergeV1JourneysCardStep conciergeV1JourneysCardStep${capitalize(step.state)}">
                        <span class="conciergeV1JourneysCardStepTitle">${escapeHtml(step.title)}</span>
                        <span class="conciergeV1JourneysCardStepBody">${escapeHtml(step.body)}</span>
                        ${dateHTML}
                        ${trackingHTML}
                    </li>
                `;
            });

            let labelHTML = "";
            if (entry.has_label) {
                labelHTML = `<button class="conciergeV1Button conciergeV1ButtonSecondary conciergeV1JourneysCardLabel" data-request="${escapeHtml(entry.request_number)}">Print your shipping label</button>`;
            }

            html += `
                <article class="conciergeV1JourneysCard">
                    <header class="conciergeV1JourneysCardHeader">
                        <h3 class="conciergeV1JourneysCardHeaderTitle">${escapeHtml(entry.product_title)}</h3>
                        <p class="conciergeV1JourneysCardHeaderMeta">${escapeHtml(entry.summary)} &middot; ${escapeHtml(entry.order_name)} &middot; ${escapeHtml(entry.request_number)}</p>
                    </header>
                    <ol class="conciergeV1JourneysCardSteps">${stepsHTML}</ol>
                    ${labelHTML}
                </article>
            `;
        });

        container.innerHTML = html;
    }

    /********************* Submit *********************/

    // gather every answer and send it
    async function submitRequest() {
        const payload = {
            // no order id is ever sent: the API resolves it from these details,
            // so a tampered payload cannot point at somebody else's order
            order_number: auth.order_number,
            email: auth.email,
            zip: auth.zip,
            intent: request.intent,
            items: buildItems(),
            customer_notes: notesForIntent(),
            photos: photos,
            customer_address: readAddress(),
            preferred_contact: section.querySelector(".conciergeV1ContactItemInput:checked").value,
        };

        if (request.intent === "repair") {
            payload.repair_type = request.repair_type;
            payload.repair_type_other = section.querySelector(".conciergeV1RepairOther").value.trim();
        } else {
            payload.resolution = request.resolution;
            payload.reason = section.querySelector(".conciergeV1ReasonSelect").value;

            // the API decides return vs exchange from the resolution, so send
            // the intent that matches what was actually chosen
            payload.intent = "return";
            if (request.resolution === "exchange_size" || request.resolution === "exchange_piece") {
                payload.intent = "exchange";
            }
        }

        setBusy(true);
        const response = await post(`${API}/submit.php`, payload);
        setBusy(false);

        if (!response) {
            return;
        }

        const numbers = response.request_numbers || [];

        let intro = "Our Concierge will review your request within 1 business day. If approved, we'll email you a prepaid shipping label along with next steps.";
        if (request.intent === "repair") {
            intro = "Our Concierge will review your request! If approved, we'll email you a prepaid shipping label along with next steps for repair.";
        }
        if (numbers.length > 1) {
            intro = `Our Concierge will review your ${numbers.length} pieces within 1 business day. If approved, we'll email you a prepaid shipping label along with next steps.`;
        }
        section.querySelector(".conciergeV1DoneIntro").textContent = intro;

        showScreen("done");
    }

    // one entry per chosen piece: shared answers stay on the payload, and the
    // size question is answered per piece because sizes differ per piece
    function buildItems() {
        return selectedLineItems().map(item => {
            const entry = {
                order_line_item_id: item.id,
                quantity: item.quantity || 1,
            };

            const row = section.querySelector(`.conciergeV1SizesItem[data-line-item-id="${item.id}"]`);
            if (row) {
                entry.still_deciding = row.querySelector(".conciergeV1SizesItemDeciding").checked;
                entry.requested_size = row.querySelector(".conciergeV1SizesItemInput").value.trim();
            }

            return entry;
        });
    }

    // the notes box lives on a different screen per path
    function notesForIntent() {
        if (request.intent === "repair") {
            return section.querySelector(".conciergeV1RepairNotes").value.trim();
        }
        return section.querySelector(".conciergeV1ReasonNotes").value.trim();
    }

    // read the address fields into the shape the label buyer expects
    function readAddress() {
        return {
            name: section.querySelector(".conciergeV1AddressName").value.trim(),
            street1: section.querySelector(".conciergeV1AddressStreet1").value.trim(),
            street2: section.querySelector(".conciergeV1AddressStreet2").value.trim(),
            city: section.querySelector(".conciergeV1AddressCity").value.trim(),
            state: section.querySelector(".conciergeV1AddressState").value.trim(),
            zip: section.querySelector(".conciergeV1AddressZip").value.trim(),
            country: (order.shipping_address || {}).country || "US",
            phone: section.querySelector(".conciergeV1AddressPhone").value.trim(),
            email: section.querySelector(".conciergeV1AddressEmail").value.trim(),
        };
    }

    // a label cannot be bought without a real collection address
    function validateAddress() {
        const address = readAddress();

        if (!address.name || !address.street1 || !address.city || !address.state || !address.zip) {
            showMessage("We need your full address so we can send you a shipping label.");
            return false;
        }

        if (!address.email) {
            showMessage("We need an email address to send your label to.");
            return false;
        }

        return true;
    }

    /********************* Photos *********************/

    // each file goes straight to storage; only the returned key is submitted
    async function uploadPhotos(files) {
        setBusy(true);

        for (const file of files) {
            // the presigner mints the key, so the browser never builds a path
            const upload = await post(`${API}/photo-upload-url.php`, auth);
            if (!upload) {
                showMessage("We couldn't upload that photo. You can skip this step and email it to us instead.");
                break;
            }

            const stored = await fetch(upload.upload_url, {
                method: "PUT",
                body: file,
            }).catch(() => null);

            if (!stored || !stored.ok) {
                showMessage("We couldn't upload that photo. You can skip this step and email it to us instead.");
                break;
            }

            photos.push(upload.file_id);
        }

        setBusy(false);
        renderPhotoList();
        renderNav("photos");
    }

    function renderPhotoList() {
        const container = section.querySelector(".conciergeV1UploadList");

        let html = "";
        photos.forEach((key, index) => {
            html += `<span class="conciergeV1UploadListItem">Photo ${index + 1}</span>`;
        });
        container.innerHTML = html;
    }

    /********************* Helpers *********************/

    // no custom headers, so the browser treats this as a simple cross-origin
    // request and never fires a preflight
    async function post(url, payload) {
        const response = await fetch(url, {
            method: "POST",
            body: JSON.stringify(payload),
        }).then(r => r.json()).catch(() => null);

        return unwrap(response);
    }

    // the API answers errors with a 200 and an error property, never a status
    function unwrap(response) {
        if (!response) {
            showMessage("Something went wrong. Please try again, or email hi@audryrosejewelry.com.");
            return null;
        }

        if (response.error) {
            showMessage(response.error);
            return null;
        }

        return response;
    }

    function showMessage(text) {
        const message = section.querySelector(".conciergeV1Message");
        message.textContent = text;
        message.classList.add("active");
    }

    function clearMessage() {
        const message = section.querySelector(".conciergeV1Message");
        message.textContent = "";
        message.classList.remove("active");
    }

    function setBusy(busy) {
        section.classList.toggle("conciergeV1Busy", busy);
        nav.querySelector(".conciergeV1NavContinue").disabled = busy;
    }

    function escapeHtml(text) {
        if (!text) {
            return "";
        }
        const div = document.createElement("div");
        div.textContent = text;
        return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    }

    function capitalize(text) {
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    /********************* Events *********************/

    // one delegated handler for every choice in the wizard
    section.addEventListener("click", async (e) => {
        // both the two path cards and the "check on a request" button set an
        // intent and land on the same lookup screen
        const intent = e.target.closest("[data-intent]");
        if (intent) {
            request = {intent: intent.dataset.intent};
            auth = {};
            pendingRequest = "";
            order = null;
            selectedItems = [];
            photos = [];
            screenIndex = 1;
            await enterScreen("order");
            return;
        }

        const pieceButton = e.target.closest(".conciergeV1PiecesItem");
        if (pieceButton) {
            // a piece we cannot help with is shown with its reason, not hidden,
            // so the customer understands rather than wonders where it went
            if (pieceButton.classList.contains("conciergeV1PiecesItemDisabled")) {
                return;
            }
            const id = pieceButton.dataset.lineItemId;

            // a repair is one piece, so picking it is also the answer
            if (request.intent === "repair") {
                selectedItems = [id];
                renderPieces();
                await goForward();
                return;
            }

            // a return or exchange can cover several, so this only toggles and
            // the customer presses Continue when they are done
            if (selectedItems.includes(id)) {
                selectedItems = selectedItems.filter(entry => entry !== id);
            } else {
                selectedItems.push(id);
            }
            renderPieces();
            clearMessage();
            return;
        }

        if (e.target.closest(".conciergeV1DoneStatus")) {
            await renderJourneys();
            showScreen("status");
            return;
        }

        const labelButton = e.target.closest(".conciergeV1JourneysCardLabel");
        if (labelButton) {
            const label = await post(`${API}/label-url.php`, {
                order_number: auth.order_number,
                email: auth.email,
                zip: auth.zip,
                request: labelButton.dataset.request,
            });
            if (label) {
                window.open(label.url, "_blank");
            }
            return;
        }

        if (e.target.closest(".conciergeV1NavBack")) {
            goBack();
            return;
        }

        if (e.target.closest(".conciergeV1NavContinue")) {
            await goForward();
        }
    });

    // radio choices update state as they are picked
    section.addEventListener("change", (e) => {
        const resolution = e.target.closest(".conciergeV1OptionsResolution .conciergeV1OptionsItemInput");
        if (resolution) {
            request.resolution = resolution.value;
            return;
        }

        const repair = e.target.closest(".conciergeV1OptionsRepair .conciergeV1OptionsItemInput");
        if (repair) {
            request.repair_type = repair.value;
            // the free text box only earns its place when nothing else fitted
            section.querySelector(".conciergeV1FieldRepairOther").classList.toggle("active", repair.value === "other");
            return;
        }

        // "still deciding" and a typed size are mutually exclusive answers
        const deciding = e.target.closest(".conciergeV1SizesItemDeciding");
        if (deciding) {
            deciding.closest(".conciergeV1SizesItem")
                .querySelector(".conciergeV1SizesItemInput").disabled = deciding.checked;
            return;
        }

        const upload = e.target.closest(".conciergeV1UploadInput");
        if (upload && upload.files.length > 0) {
            uploadPhotos(Array.from(upload.files));
        }
    });

    // enter should submit the lookup rather than doing nothing
    section.addEventListener("keydown", (e) => {
        if (e.key !== "Enter") {
            return;
        }
        if (!e.target.closest(".conciergeV1ScreenOrder")) {
            return;
        }
        e.preventDefault();
        goForward();
    });

    /********************* Init *********************/

    (async () => {
        // an emailed link names the request but carries no credential, so the
        // customer looks their order up first. that keeps their email address
        // out of a URL that would otherwise reach analytics and browser history
        const requested = new URLSearchParams(window.location.search).get("request");
        if (requested) {
            request = {intent: "status"};
            pendingRequest = requested;
            screenIndex = 1;
            section.querySelector(".conciergeV1ScreenOrder .conciergeV1ScreenIntro").textContent =
                `To follow ${requested}, confirm the order it belongs to.`;
            await enterScreen("order");
            return;
        }

        showScreen("intent");
    })();
}());
